// src/components/ModalResumenCargosAutomaticos.js
import React, { useState } from 'react';
import { FaTimes, FaSearch, FaChevronDown, FaChevronUp, FaCheck } from 'react-icons/fa';
import './ModalResumenCargosAutomaticos.css';

const ModalResumenCargosAutomaticos = ({ resumen, onClose, onMarkAsRead }) => {
    const [filtro, setFiltro] = useState('');
    const [expandedCasaId, setExpandedCasaId] = useState(null);

    if (!resumen) return null;

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-MX', {
            style: 'currency',
            currency: 'MXN'
        }).format(amount || 0);
    };

    const formatDate = (date) => {
        if (!date) return 'N/A';
        const fecha = date.toDate ? date.toDate() : new Date(date);
        return fecha.toLocaleDateString('es-MX', {
            day: '2-digit',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleCerrar = () => {
        if (onMarkAsRead && !resumen.visto) {
            onMarkAsRead(resumen.id);
        }
        onClose();
    };

    // Filtrar los detalles de las casas
    const detallesFiltrados = (resumen.detallesCasas || []).filter(detalle => 
        String(detalle.numero || detalle.numeroCasa || '').toLowerCase().includes(filtro.toLowerCase()) ||
        String(detalle.nombre || detalle.nombre_residente || '').toLowerCase().includes(filtro.toLowerCase())
    );

    // Calcular valores de tarjetas con soporte de retrocompatibilidad
    const esResumenNuevo = resumen.detallesCasas && resumen.detallesCasas.length > 0 && ('cargoVisible' in resumen.detallesCasas[0] || 'cargoCapitalDelMes' in resumen.detallesCasas[0]);

    const totalCargado = esResumenNuevo 
        ? resumen.detallesCasas.reduce((acc, d) => {
            const mtoExtras = d.montoCuotasExtras !== undefined 
                ? Number(d.montoCuotasExtras || 0)
                : Number(d.desglose?.cuotasExtras || 0);
            
            const mtoConsumo = d.montoConsumoIndividualizado !== undefined
                ? Number(d.montoConsumoIndividualizado || 0)
                : Number(d.desglose?.consumoIndividualizado || 0);
            
            const mtoRecargo = d.interesMoraDelMes !== undefined
                ? Number(d.interesMoraDelMes || 0)
                : Number(d.desglose?.interesesMora || 0);
            
            const mtoMantenimiento = d.cargoCapitalDelMes !== undefined
                ? (Number(d.cargoCapitalDelMes || 0) - mtoExtras - mtoConsumo)
                : Number(d.desglose?.cuotaBase || 0);
            
            return acc + (mtoMantenimiento + mtoConsumo + mtoExtras + mtoRecargo);
        }, 0)
        : (resumen.totalAdeudo || 0);

    const cubiertoSaldoFavor = esResumenNuevo
        ? resumen.detallesCasas.reduce((acc, d) => acc + (d.montoDescontadoDeSaldoFavor || 0), 0)
        : 0;

    const cargadoDeudaNeto = esResumenNuevo
        ? resumen.detallesCasas.reduce((acc, d) => {
            const mtoExtras = d.montoCuotasExtras !== undefined 
                ? Number(d.montoCuotasExtras || 0)
                : Number(d.desglose?.cuotasExtras || 0);
            
            const mtoConsumo = d.montoConsumoIndividualizado !== undefined
                ? Number(d.montoConsumoIndividualizado || 0)
                : Number(d.desglose?.consumoIndividualizado || 0);
            
            const mtoRecargo = d.interesMoraDelMes !== undefined
                ? Number(d.interesMoraDelMes || 0)
                : Number(d.desglose?.interesesMora || 0);
            
            const mtoMantenimiento = d.cargoCapitalDelMes !== undefined
                ? (Number(d.cargoCapitalDelMes || 0) - mtoExtras - mtoConsumo)
                : Number(d.desglose?.cuotaBase || 0);
            
            const totalCasa = mtoMantenimiento + mtoConsumo + mtoExtras + mtoRecargo;
            const descSaldo = d.montoDescontadoDeSaldoFavor || 0;
            return acc + Math.max(0, totalCasa - descSaldo);
        }, 0)
        : (resumen.totalAdeudo || 0);

    return (
        <div className="modal-resumen-backdrop" onClick={handleCerrar}>
            <div 
                className="modal-resumen-content" 
                onClick={(e) => e.stopPropagation()}
                style={{
                    maxWidth: '900px',
                    width: '95%',
                    maxHeight: '90vh',
                    display: 'flex',
                    flexDirection: 'column'
                }}
            >
                {/* Cabecera con gradiente morado elegante (identifica cargo automático) */}
                <div className="modal-resumen-header" style={{ flexShrink: 0 }}>
                    <div className="modal-resumen-header-left">
                        <div className="success-icon-wrapper" style={{ backgroundColor: 'rgba(255, 255, 255, 0.2)', width: '40px', height: '40px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <FaCheck style={{ color: 'white', fontSize: '18px' }} />
                        </div>
                        <div>
                            <h2>Resumen de Cargos Automáticos</h2>
                            <p className="modal-resumen-subtitle">
                                Ejecutado el {formatDate(resumen.fechaEjecucion)}
                            </p>
                        </div>
                    </div>
                    <button type="button" className="modal-resumen-close" onClick={handleCerrar}>
                        <FaTimes />
                    </button>
                </div>

                <div className="modal-resumen-body" style={{ 
                    overflowY: 'auto', 
                    padding: '20px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px'
                }}>
                    {/* Alerta verde superior */}
                    <div style={{
                        backgroundColor: '#ecfdf5',
                        borderLeft: '4px solid #10b981',
                        padding: '12px 16px',
                        borderRadius: '4px',
                        color: '#065f46',
                        fontSize: '14px',
                        fontWeight: '500'
                    }}>
                        Cargos mensuales aplicados automáticamente a {resumen.totalCasasAfectadas || 0} casas. Fases ejecutadas: {resumen.faseEjecutada || 'corte'}.
                    </div>

                    {/* Tarjetas de Resumen General */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                        gap: '12px',
                        backgroundColor: '#f8fafc',
                        padding: '16px',
                        borderRadius: '12px',
                        border: '1px solid #e2e8f0'
                    }}>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <span style={{ fontSize: '11px', color: '#64748b', fontWeight: '600' }}>CASAS ACTUALIZADAS</span>
                            <span style={{ fontSize: '18px', color: '#1e293b', fontWeight: '700' }}>{resumen.totalCasasAfectadas || 0}</span>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <span style={{ fontSize: '11px', color: '#64748b', fontWeight: '600' }}>TOTAL CARGADO</span>
                            <span style={{ fontSize: '18px', color: '#10b981', fontWeight: '700' }}>
                                {formatCurrency(totalCargado)}
                            </span>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <span style={{ fontSize: '11px', color: '#64748b', fontWeight: '600' }}>CUBIERTO CON SALDO A FAVOR</span>
                            <span style={{ fontSize: '18px', color: '#3b82f6', fontWeight: '700' }}>
                                {formatCurrency(cubiertoSaldoFavor)}
                            </span>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <span style={{ fontSize: '11px', color: '#64748b', fontWeight: '600' }}>CARGADO A DEUDA NETO</span>
                            <span style={{ fontSize: '18px', color: '#ef4444', fontWeight: '700' }}>
                                {formatCurrency(cargadoDeudaNeto)}
                            </span>
                        </div>
                    </div>

                    {/* Detalle por Casa */}
                    <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                            <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>Detalle por Casa</h3>
                            <span style={{ fontSize: '11px', color: '#64748b' }}>
                                Mostrando {detallesFiltrados.length} de {(resumen.detallesCasas || []).length} casas
                            </span>
                        </div>

                        {/* Input de Búsqueda */}
                        <div style={{ position: 'relative', marginBottom: '12px' }}>
                            <FaSearch style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: '13px' }} />
                            <input 
                                type="text" 
                                placeholder="Buscar por número de casa o residente..." 
                                value={filtro} 
                                onChange={(e) => setFiltro(e.target.value)} 
                                style={{
                                    width: '100%',
                                    padding: '8px 12px 8px 36px',
                                    borderRadius: '8px',
                                    border: '1px solid #cbd5e1',
                                    fontSize: '13px',
                                    boxSizing: 'border-box',
                                    outline: 'none'
                                }}
                            />
                        </div>

                        {/* Tabla Desplazable */}
                        <div style={{ 
                            border: '1px solid #e2e8f0', 
                            borderRadius: '8px', 
                            overflow: 'hidden',
                            maxHeight: '280px',
                            overflowY: 'auto'
                        }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', textAlign: 'left' }}>
                                <thead style={{ backgroundColor: '#f1f5f9', position: 'sticky', top: 0, zIndex: 1 }}>
                                    <tr>
                                        <th style={{ padding: '10px 12px', fontWeight: '600', color: '#475569', borderBottom: '1px solid #e2e8f0' }}>Casa</th>
                                        <th style={{ padding: '10px 12px', fontWeight: '600', color: '#475569', borderBottom: '1px solid #e2e8f0' }}>Residente</th>
                                        <th style={{ padding: '10px 12px', fontWeight: '600', color: '#475569', borderBottom: '1px solid #e2e8f0' }}>Desglose de Cargos</th>
                                        <th style={{ padding: '10px 12px', fontWeight: '600', color: '#475569', borderBottom: '1px solid #e2e8f0', textAlign: 'right' }}>Monto Aplicado</th>
                                        <th style={{ padding: '10px 12px', fontWeight: '600', color: '#475569', borderBottom: '1px solid #e2e8f0', textAlign: 'right' }}>Nuevo Adeudo</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {detallesFiltrados.map((detalle, idx) => {
                                        // Unificar variables con soporte de retrocompatibilidad
                                        const mtoExtras = detalle.montoCuotasExtras !== undefined 
                                            ? Number(detalle.montoCuotasExtras || 0)
                                            : Number(detalle.desglose?.cuotasExtras || 0);
                                        
                                        const mtoConsumo = detalle.montoConsumoIndividualizado !== undefined
                                            ? Number(detalle.montoConsumoIndividualizado || 0)
                                            : Number(detalle.desglose?.consumoIndividualizado || 0);
                                        
                                        const mtoRecargo = detalle.interesMoraDelMes !== undefined
                                            ? Number(detalle.interesMoraDelMes || 0)
                                            : Number(detalle.desglose?.interesesMora || 0);
                                        
                                        const mtoMantenimiento = detalle.cargoCapitalDelMes !== undefined
                                            ? (Number(detalle.cargoCapitalDelMes || 0) - mtoExtras - mtoConsumo)
                                            : Number(detalle.desglose?.cuotaBase || 0);

                                        const cargoVisible = detalle.cargoVisible !== undefined
                                            ? Number(detalle.cargoVisible || 0)
                                            : (mtoMantenimiento + mtoConsumo + mtoExtras + mtoRecargo);

                                        const montoDescontadoDeSaldoFavor = detalle.montoDescontadoDeSaldoFavor !== undefined
                                            ? Number(detalle.montoDescontadoDeSaldoFavor || 0)
                                            : Number(detalle.desglose?.saldoFavorPrevio || 0);

                                        const nuevoAdeudo = detalle.nuevoAdeudo !== undefined
                                            ? Number(detalle.nuevoAdeudo || 0)
                                            : Number(detalle.adeudo || 0);

                                        const saldoFavorAnterior = detalle.saldoFavorAnterior !== undefined
                                            ? Number(detalle.saldoFavorAnterior || 0)
                                            : Number(detalle.saldoFavor || 0);

                                        const adeudoAnterior = detalle.adeudoAnterior !== undefined
                                            ? Number(detalle.adeudoAnterior || 0)
                                            : Number(detalle.desglose?.adeudoAnterior || 0);

                                        const nuevoSaldoFavor = detalle.nuevoSaldoFavor !== undefined
                                            ? Number(detalle.nuevoSaldoFavor || 0)
                                            : Number(detalle.saldoFavor || 0);

                                        const isExpanded = expandedCasaId === detalle.id;

                                        return (
                                            <React.Fragment key={detalle.id || idx}>
                                                <tr 
                                                    style={{ 
                                                        borderBottom: '1px solid #f1f5f9', 
                                                        cursor: 'pointer',
                                                        backgroundColor: isExpanded ? '#f8fafc' : 'transparent'
                                                    }}
                                                    onClick={() => setExpandedCasaId(isExpanded ? null : detalle.id)}
                                                >
                                                    <td style={{ padding: '8px 12px', fontWeight: '600', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                        <span style={{ fontSize: '10px', color: '#64748b' }}>{isExpanded ? '▼' : '►'}</span>
                                                        {detalle.numero || detalle.numeroCasa || 'N/A'}
                                                    </td>
                                                    <td style={{ padding: '8px 12px', color: '#334155' }}>{detalle.nombre_residente || detalle.nombre || 'Sin Nombre'}</td>
                                                    <td style={{ padding: '8px 12px', color: '#475569', fontSize: '11px', lineHeight: '1.4' }}>
                                                        {mtoMantenimiento > 0 && (
                                                            <div>• Mant.: {formatCurrency(mtoMantenimiento)}</div>
                                                        )}
                                                        {mtoConsumo > 0 && (
                                                            <div>• Consumo: {formatCurrency(mtoConsumo)}</div>
                                                        )}
                                                        {mtoExtras > 0 && (
                                                            <div style={{ color: '#6366f1' }}>
                                                                • Extra: {formatCurrency(mtoExtras)}
                                                            </div>
                                                        )}
                                                        {mtoRecargo > 0 && (
                                                            <div style={{ color: '#dc2626', fontWeight: '500' }}>• Recargo: {formatCurrency(mtoRecargo)}</div>
                                                        )}
                                                        {mtoMantenimiento <= 0 && mtoConsumo <= 0 && mtoExtras <= 0 && mtoRecargo <= 0 && (
                                                            <span style={{ color: '#64748b' }}>Sin cargos</span>
                                                        )}
                                                    </td>
                                                    <td style={{ 
                                                        padding: '8px 12px', 
                                                        color: montoDescontadoDeSaldoFavor > 0 ? '#2563eb' : '#ef4444', 
                                                        textAlign: 'right', 
                                                        fontWeight: '500' 
                                                    }}>
                                                        {montoDescontadoDeSaldoFavor > 0 
                                                            ? formatCurrency(montoDescontadoDeSaldoFavor) 
                                                            : ((cargoVisible - montoDescontadoDeSaldoFavor) > 0 
                                                                ? `-${formatCurrency(cargoVisible - montoDescontadoDeSaldoFavor)}` 
                                                                : '-')}
                                                    </td>
                                                    <td style={{ 
                                                        padding: '8px 12px', 
                                                        color: nuevoAdeudo > 0 ? '#ef4444' : '#10b981', 
                                                        textAlign: 'right', 
                                                        fontWeight: '600' 
                                                    }}>
                                                        {nuevoAdeudo > 0 ? formatCurrency(nuevoAdeudo) : '✓ Liquidado'}
                                                    </td>
                                                </tr>
                                                {isExpanded && (
                                                    <tr>
                                                        <td colSpan="5" style={{ backgroundColor: '#f8fafc', padding: '16px', borderBottom: '1px solid #e2e8f0' }}>
                                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                                                                {/* Estado Anterior vs Nuevo */}
                                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                                                    <h4 style={{ margin: '0 0 8px 0', fontSize: '11px', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '600' }}>Flujo de Saldo</h4>
                                                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', borderBottom: '1px solid #e2e8f0', paddingBottom: '4px' }}>
                                                                        <span style={{ color: '#64748b' }}>Saldo a Favor Anterior:</span>
                                                                        <span style={{ fontWeight: '600', color: '#1e293b' }}>{formatCurrency(saldoFavorAnterior)}</span>
                                                                    </div>
                                                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', borderBottom: '1px solid #e2e8f0', paddingBottom: '4px' }}>
                                                                        <span style={{ color: '#64748b' }}>Adeudo Anterior:</span>
                                                                        <span style={{ fontWeight: '600', color: '#e11d48' }}>{formatCurrency(adeudoAnterior)}</span>
                                                                    </div>
                                                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', borderBottom: '1px solid #e2e8f0', paddingBottom: '4px', backgroundColor: '#eff6ff', padding: '4px 6px', borderRadius: '4px' }}>
                                                                        <span style={{ color: '#2563eb', fontWeight: '500' }}>Aplicado de Saldo a Favor:</span>
                                                                        <span style={{ fontWeight: '700', color: '#2563eb' }}>-{formatCurrency(montoDescontadoDeSaldoFavor)}</span>
                                                                    </div>
                                                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', borderBottom: '1px solid #e2e8f0', paddingBottom: '4px' }}>
                                                                        <span style={{ color: '#64748b' }}>Nuevo Saldo a Favor:</span>
                                                                        <span style={{ fontWeight: '600', color: '#15803d' }}>{formatCurrency(nuevoSaldoFavor)}</span>
                                                                    </div>
                                                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
                                                                        <span style={{ color: '#64748b' }}>Nuevo Adeudo Total:</span>
                                                                        <span style={{ fontWeight: '700', color: nuevoAdeudo > 0 ? '#e11d48' : '#15803d' }}>{formatCurrency(nuevoAdeudo)}</span>
                                                                    </div>
                                                                </div>

                                                                {/* Desglose de Cargos Aplicados */}
                                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                                                    <h4 style={{ margin: '0 0 8px 0', fontSize: '11px', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '600' }}>Desglose de Cargos del Mes</h4>
                                                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', borderBottom: '1px solid #e2e8f0', paddingBottom: '4px' }}>
                                                                        <span style={{ color: '#64748b' }}>Cuota de Mantenimiento:</span>
                                                                        <span style={{ fontWeight: '600', color: '#1e293b' }}>{formatCurrency(mtoMantenimiento)}</span>
                                                                    </div>
                                                                    {mtoConsumo > 0 && (
                                                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', borderBottom: '1px solid #e2e8f0', paddingBottom: '4px' }}>
                                                                            <span style={{ color: '#64748b' }}>Consumo Individualizado:</span>
                                                                            <span style={{ fontWeight: '600', color: '#1e293b' }}>{formatCurrency(mtoConsumo)}</span>
                                                                        </div>
                                                                    )}
                                                                    {mtoExtras > 0 && (
                                                                        <div style={{ display: 'flex', flexDirection: 'column', borderBottom: '1px solid #e2e8f0', paddingBottom: '4px' }}>
                                                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
                                                                                <span style={{ color: '#64748b' }}>Cuotas Extraordinarias:</span>
                                                                                <span style={{ fontWeight: '600', color: '#6366f1' }}>{formatCurrency(mtoExtras)}</span>
                                                                            </div>
                                                                            <div style={{ fontSize: '10px', color: '#6366f1', marginTop: '2px', fontStyle: 'italic' }}>
                                                                                ↳ {(detalle.cuotasExtrasAplicadas || []).map(e => `${e.descripcion} (${formatCurrency(e.monto || 0)})`).join(', ')}
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                    {mtoRecargo > 0 && (
                                                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', borderBottom: '1px solid #e2e8f0', paddingBottom: '4px' }}>
                                                                            <span style={{ color: '#dc2626' }}>Recargo por Morosidad:</span>
                                                                            <span style={{ fontWeight: '700', color: '#dc2626' }}>{formatCurrency(mtoRecargo)}</span>
                                                                        </div>
                                                                    )}
                                                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', fontWeight: '700', paddingTop: '4px' }}>
                                                                        <span style={{ color: '#1e293b' }}>Total Cargado:</span>
                                                                        <span style={{ color: '#1e293b' }}>{formatCurrency(mtoMantenimiento + mtoConsumo + mtoExtras + mtoRecargo)}</span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                    {detallesFiltrados.length === 0 && (
                                        <tr>
                                            <td colSpan="5" style={{ padding: '24px', textAlign: 'center', color: '#64748b' }}>
                                                No se encontraron viviendas con el filtro especificado.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* Botón Aceptar centrado abajo en verde */}
                <div className="modal-resumen-footer" style={{ flexShrink: 0, padding: '16px 20px 20px', display: 'flex', justifyContent: 'center', backgroundColor: '#ffffff' }}>
                    <button
                        className="btn-success-accept"
                        onClick={handleCerrar}
                        style={{
                            padding: '10px 32px',
                            backgroundColor: '#10b981',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '14px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s',
                            boxShadow: '0 4px 6px -1px rgba(16, 185, 129, 0.2), 0 2px 4px -1px rgba(16, 185, 129, 0.1)'
                        }}
                        onMouseOver={(e) => e.target.style.backgroundColor = '#059669'}
                        onMouseOut={(e) => e.target.style.backgroundColor = '#10b981'}
                    >
                        Aceptar
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ModalResumenCargosAutomaticos;
